/**
 * Prop Studio's Floors & lighting: the floors and the HDRI lighting each kind
 * of space is rendered with (renderFinish.ts — the library's `finishes`).
 * Stage draws one of each for a room of that kind and the plan keeps the draw.
 *
 * The spaces down the left with what each has; for the one chosen, its floors
 * as swatch cards (Tune opens the live preview on the entry; remove takes it
 * out) and its lighting as cards with strength and turn. Below each, the
 * library to add from, and a finder for more in a window of its own (Poly
 * Haven; BlenderKit where the back end has Blender; an HDRI upload). Tune is
 * a live Three.js preview (FloorTuner): nothing is rendered on a GPU worker.
 * Every change is saved to the library as it is made.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Plus, Search, SlidersHorizontal, Trash2 } from 'lucide-react';
import { SPACE_TYPES } from '../../data/presets';
import {
  HdriEntry, LIBRARY_CHANGED_EVENT, ServiceDownError, TextureSet, getLibrary, listHdri, saveFinishes, textureLibrary, textureMapUrl,
} from './studioApi';
import { FINISH_MAX, FinishPools, FloorFinish, HdriFinish, SpaceFinishes } from '../ui/room3d/renderFinish';
import { FACTORY_PHOTO_RENDER, PLACEMENT_FACTORY, RANGES, TexturePlacement } from '../ui/room3d/photoRender';
import { HdriFinder, TextureFinder } from '../designer/render/LibraryFinders';
import { DesignerModal } from '../designer/chrome/DesignerModal';
import { FloorTuner } from './FloorTuner';
import { MiniSlider } from '../designer/render/controls';
import { DESIGNER_STRINGS } from '../designer/strings';
import { PROP_STUDIO_STRINGS } from './strings';
import '../designer/render/renderControls.css';

const F = PROP_STUDIO_STRINGS.finishes;
const ACCENT = '#FEB100';
const MUTED = '#a9abb3';
const SPACE_KEY = 'propStudio.finishSpace';
const SAVE_DELAY_MS = 450;

const EMPTY: SpaceFinishes = { floor: [], hdri: [] };
const say = (err: unknown) => (err instanceof ServiceDownError ? DESIGNER_STRINGS.photo.hdriNoBackEnd : err instanceof Error ? err.message : String(err));
const setThumb = (t: TextureSet | undefined) => (t ? t.thumbnail ?? (t.maps.diffuse ? textureMapUrl(t.id, t.maps.diffuse) : null) : null);
const setSize = (t: TextureSet) => DESIGNER_STRINGS.photo.textureSize(t.dimensions[0] / 304.8, t.dimensions[1] / 304.8);

const card: React.CSSProperties = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' };
const swatch = (url: string | null): React.CSSProperties => ({ height: 120, background: url ? `#2a2b31 center / cover no-repeat url(${url})` : 'repeating-linear-gradient(45deg,#2a2b31 0 8px,#24252a 8px 16px)' });
const iconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, height: 26, padding: '0 9px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 12, cursor: 'pointer' };
const heading: React.CSSProperties = { margin: '22px 0 10px', fontSize: 15, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'baseline', gap: 10 };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 };
const panel: React.CSSProperties = { marginTop: 14, padding: 12, borderRadius: 10, background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(255,255,255,0.08)' };

export const FinishesManager: React.FC = () => {
  const [pools, setPools] = useState<FinishPools | null>(null);
  const [sets, setSets] = useState<TextureSet[] | null>(null);
  const [hdris, setHdris] = useState<HdriEntry[] | null>(null);
  const [space, setSpace] = useState<string>(() => { try { return window.localStorage.getItem(SPACE_KEY) || 'garage'; } catch { return 'garage'; } });
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [tuning, setTuning] = useState<number | null>(null);
  const [finding, setFinding] = useState<'floors' | 'lighting' | null>(null);
  // Whether the back end can bake BlenderKit's materials (it has Blender: a PC, not a web server).
  const [blender, setBlender] = useState(true);

  const refreshSets = useCallback(() => textureLibrary().then((lib) => { setSets(lib.textures); setBlender(lib.blender); }).catch((err) => { setSets([]); setError(say(err)); }), []);
  const refreshHdris = useCallback(() => listHdri().then(setHdris).catch((err) => { setHdris([]); setError(say(err)); }), []);
  useEffect(() => {
    getLibrary().then((lib) => setPools(lib.finishes ?? {})).catch((err) => { setPools({}); setError(say(err)); });
    refreshSets();
    refreshHdris();
  }, [refreshSets, refreshHdris]);

  // Saving: the whole pools, a moment after the last change, one at a time.
  const pending = useRef<FinishPools | null>(null);
  const timer = useRef<number | null>(null);
  const flush = useCallback(async () => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setStatus('saving');
    try {
      await saveFinishes(next);
      setError(null);
      setStatus(pending.current ? 'saving' : 'saved');
      window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
    } catch (err) { setError(say(err)); setStatus('idle'); }
  }, []);
  useEffect(() => () => { if (timer.current !== null) { window.clearTimeout(timer.current); flush(); } }, [flush]);

  const mine = pools?.[space] ?? EMPTY;
  // A change to this space's entries, from what they are when it lands (a download finishing late adds to the latest).
  const change = (edit: (now: SpaceFinishes) => SpaceFinishes) => {
    setPools((p) => {
      const all = { ...(p ?? {}), [space]: edit(p?.[space] ?? EMPTY) };
      pending.current = all;
      return all;
    });
    setStatus('saving');
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { timer.current = null; flush(); }, SAVE_DELAY_MS);
  };
  const setFloors = (floor: FloorFinish[]) => change((now) => ({ ...now, floor }));
  const setLights = (hdri: HdriFinish[]) => change((now) => ({ ...now, hdri }));

  const chooseSpace = (id: string) => { setSpace(id); setTuning(null); try { window.localStorage.setItem(SPACE_KEY, id); } catch { /* not kept — fine */ } };
  const addFloor = (id: string) => change((now) => (now.floor.length < FINISH_MAX ? { ...now, floor: [...now.floor, { id, ...PLACEMENT_FACTORY }] } : now));
  const addLight = (file: string) => change((now) => (now.hdri.length < FINISH_MAX
    ? { ...now, hdri: [...now.hdri, { file, strength: FACTORY_PHOTO_RENDER.light.hdriStrength, rotation: FACTORY_PHOTO_RENDER.light.hdriRotation }] }
    : now));

  const spaceLabel = SPACE_TYPES.find((s) => s.id === space)?.label ?? space;
  const setOf = (id: string) => sets?.find((t) => t.id === id);
  const hdriOf = (file: string) => hdris?.find((h) => h.file === file);
  const [lo, hi] = RANGES['light.hdriStrength'];

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <aside style={{ width: 230, flex: '0 0 auto', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.1)', padding: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '2px 4px 8px' }}>{F.spaces}</div>
        {SPACE_TYPES.map((s) => {
          const active = s.id === space;
          const p = pools?.[s.id] ?? EMPTY;
          return (
            <button
              key={s.id} type="button" aria-pressed={active} onClick={() => chooseSpace(s.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px', marginBottom: 3, borderRadius: 7, cursor: 'pointer', color: '#fff',
                background: active ? 'rgba(254,177,0,0.16)' : 'transparent', border: active ? `1px solid ${ACCENT}` : '1px solid transparent',
              }}
            >
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{s.label}</span>
              <span style={{ display: 'block', fontSize: 11, color: p.floor.length || p.hdri.length ? '#cfd2da' : '#6f727c' }}>{F.count(p.floor.length, p.hdri.length)}</span>
            </button>
          );
        })}
      </aside>

      <main style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '16px 22px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#fff' }}>{spaceLabel}</h2>
          <span role="status" style={{ fontSize: 12, color: status === 'saved' ? '#9fe0b0' : MUTED, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {status === 'saving' && F.saving}
            {status === 'saved' && <><Check size={13} /> {F.saved}</>}
          </span>
        </div>
        <p style={{ margin: '6px 0 0', color: MUTED, fontSize: 13, maxWidth: 760, lineHeight: 1.45 }}>{space === 'other' ? F.introOther : F.intro(spaceLabel)}</p>
        {error && <p className="rc-note is-bad">{error}</p>}
        {pools === null && <p style={{ color: MUTED }}>{F.loading}</p>}

        {pools && (
          <>
            {/* Floors */}
            <h3 style={heading}>{F.floors} <span style={{ fontSize: 12, fontWeight: 400, color: MUTED }}>{mine.floor.length >= FINISH_MAX ? F.full(FINISH_MAX) : ''}</span></h3>
            {mine.floor.length === 0 && <p style={{ color: MUTED, fontSize: 13 }}>{F.floorsNone}</p>}
            <div style={grid}>
              {mine.floor.map((e, i) => {
                const t = setOf(e.id);
                return (
                  <div key={`${e.id}-${i}`} style={card}>
                    <div style={swatch(setThumb(t))} />
                    <div style={{ padding: '9px 10px 10px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{t?.name ?? e.id}</div>
                        <div style={{ fontSize: 11, color: t ? MUTED : '#ff9f9f' }}>{t ? setSize(t) : F.missing}</div>
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{DESIGNER_STRINGS.textureStudio.placed(e)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                        <button type="button" style={iconBtn} title={F.tuneHint} disabled={!t} onClick={() => setTuning(i)}><SlidersHorizontal size={13} /> {F.tune}</button>
                        <button type="button" style={{ ...iconBtn, marginLeft: 'auto' }} title={F.remove} aria-label={`${F.remove}: ${t?.name ?? e.id}`} onClick={() => setFloors(mine.floor.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={panel}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{F.addFloor}</div>
              <p className="rc-note" style={{ marginTop: 2 }}>{F.addFloorHint}</p>
              {sets && sets.length === 0 && <p className="rc-note">{F.libraryEmpty}</p>}
              <div className="rc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}>
                {(sets ?? []).map((t) => {
                  const inSpace = mine.floor.some((e) => e.id === t.id);
                  return (
                    <div key={t.id} style={{ position: 'relative' }}>
                      <button
                        type="button" className={`rc-tile${inSpace ? ' is-active' : ''}`} title={`${t.name} · ${setSize(t)}${inSpace ? ` · ${F.inSpace}` : ''}`}
                        disabled={mine.floor.length >= FINISH_MAX} onClick={() => addFloor(t.id)} style={{ backgroundImage: `url(${setThumb(t) ?? ''})` }}
                      >
                        <span className="rc-tile-name">{t.name}</span>
                      </button>
                      {inSpace ? <Check size={13} style={{ position: 'absolute', top: 4, right: 4, color: ACCENT, pointerEvents: 'none' }} /> : <Plus size={13} style={{ position: 'absolute', top: 4, right: 4, color: '#fff', pointerEvents: 'none' }} />}
                    </div>
                  );
                })}
              </div>
              <button type="button" style={{ ...iconBtn, marginTop: 10 }} onClick={() => setFinding('floors')}><Search size={13} /> {F.findTextures}</button>
            </div>

            {/* Lighting */}
            <h3 style={heading}>{F.lighting} <span style={{ fontSize: 12, fontWeight: 400, color: MUTED }}>{mine.hdri.length >= FINISH_MAX ? F.full(FINISH_MAX) : ''}</span></h3>
            {mine.hdri.length === 0 && <p style={{ color: MUTED, fontSize: 13 }}>{F.lightingNone}</p>}
            <div style={grid}>
              {mine.hdri.map((e, i) => {
                const h = hdriOf(e.file);
                const set = (patch: Partial<HdriFinish>) => setLights(mine.hdri.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                return (
                  <div key={`${e.file}-${i}`} style={card}>
                    <div style={{ ...swatch(h?.thumbnail ?? null), height: 100 }} />
                    <div style={{ padding: '9px 10px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h?.name ?? e.file}</div>
                          {!h && <div style={{ fontSize: 11, color: '#ff9f9f' }}>{F.missingHdri}</div>}
                        </div>
                        <button type="button" style={iconBtn} title={F.remove} aria-label={`${F.remove}: ${h?.name ?? e.file}`} onClick={() => setLights(mine.hdri.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
                      </div>
                      <MiniSlider label={F.strength} value={e.strength} range="light.hdriStrength" step={0.05} onChange={(v) => set({ strength: Math.max(lo, Math.min(hi, v)) })} />
                      <MiniSlider label={F.rotation} value={e.rotation} range="light.hdriRotation" step={1} onChange={(v) => set({ rotation: v })} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={panel}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{F.addLighting}</div>
              {hdris && hdris.length === 0 && <p className="rc-note">{F.libraryEmpty}</p>}
              <div className="rc-grid is-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                {(hdris ?? []).map((h) => {
                  const inSpace = mine.hdri.some((e) => e.file === h.file);
                  return (
                    <div key={h.file} style={{ position: 'relative' }}>
                      <button
                        type="button" className={`rc-tile is-wide${inSpace ? ' is-active' : ''}`} title={`${h.name || h.file}${inSpace ? ` · ${F.inSpace}` : ''}`}
                        disabled={mine.hdri.length >= FINISH_MAX} onClick={() => addLight(h.file)} style={{ backgroundImage: h.thumbnail ? `url(${h.thumbnail})` : undefined }}
                      >
                        <span className="rc-tile-name">{h.name || h.file}</span>
                      </button>
                      {inSpace ? <Check size={13} style={{ position: 'absolute', top: 4, right: 4, color: ACCENT, pointerEvents: 'none' }} /> : <Plus size={13} style={{ position: 'absolute', top: 4, right: 4, color: '#fff', pointerEvents: 'none' }} />}
                    </div>
                  );
                })}
              </div>
              <button type="button" style={{ ...iconBtn, marginTop: 10 }} onClick={() => setFinding('lighting')}><Search size={13} /> {F.findHdri}</button>
            </div>
          </>
        )}
      </main>

      {tuning !== null && mine.floor[tuning] && (
        <FloorTuner
          initial={mine.floor[tuning] as TexturePlacement} sets={sets ?? []} lights={mine.hdri} hdris={hdris ?? []} spaceLabel={spaceLabel}
          onSave={(next) => setFloors(mine.floor.map((x, j) => (j === tuning ? next : x)))}
          onClose={() => setTuning(null)}
        />
      )}

      {finding === 'floors' && (
        <DesignerModal title={F.findTitleFloors(spaceLabel)} onClose={() => setFinding(null)} width={1100} icon={<Search size={16} color={ACCENT} />}>
          <p className="rc-note" style={{ marginTop: 0 }}>{F.findFloorsNote}</p>
          <TextureFinder wide blender={blender} onFetched={(set) => { refreshSets(); if (set) addFloor(set.id); }} />
        </DesignerModal>
      )}
      {finding === 'lighting' && (
        <DesignerModal title={F.findTitleLighting(spaceLabel)} onClose={() => setFinding(null)} width={1100} icon={<Search size={16} color={ACCENT} />}>
          <p className="rc-note" style={{ marginTop: 0 }}>{F.findLightingNote}</p>
          <HdriFinder wide onFetched={(entry) => { refreshHdris(); addLight(entry.file); }} />
        </DesignerModal>
      )}
    </div>
  );
};

export default FinishesManager;
